/**
 * Audit Log Page
 * 
 * หน้าแสดงประวัติการใช้งานระบบ
 * - ดู logs ทั้งหมด (ไม่แสดง customer และ deletedAt)
 * - กรองตาม action, entity, วันที่
 * - ค้นหา
 * - ดูรายละเอียด
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  History,
  Search,
  RefreshCw,
  Download,
  X,
  Eye,
  CheckCircle,
  XCircle,
  LogIn,
  LogOut,
  Plus,
  Edit,
  Trash2,
  Settings,
  Loader2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Wallet,
  Activity,
  AlertTriangle,
  Copy,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth'
import { toast } from 'sonner'
import { ThaiDatePicker } from '@/components/ui/date-time-picker'
import { toDateInputValue } from '@/lib/date-format'
import {
  getAuditLogService,
  AuditLog,
  AuditLogFilters,
  AuditLogStatistics,
  AuditAction,
  AuditActionLabels,
  EntityType,
  EntityTypeLabels,
  SeverityLabels,
} from '@/domain/audit-log'

// ============================================
// Constants
// ============================================

const ACTIONS: { value: AuditAction; label: string }[] = [
  { value: 'login', label: 'เข้าสู่ระบบ' },
  { value: 'logout', label: 'ออกจากระบบ' },
  { value: 'create', label: 'สร้าง' },
  { value: 'update', label: 'แก้ไข' },
  { value: 'delete', label: 'ลบ' },
  { value: 'update_setting', label: 'อัปเดตการตั้งค่า' },
  { value: 'approve', label: 'อนุมัติ' },
  { value: 'reject', label: 'ปฏิเสธ' },
  { value: 'deposit', label: 'ฝากเงิน' },
  { value: 'withdraw', label: 'ถอนเงิน' },
]

const ENTITY_TYPES: { value: EntityType; label: string }[] = [
  { value: 'Member', label: 'สมาชิก' },
  { value: 'Admin', label: 'แอดมิน' },
  { value: 'AppConfig', label: 'ตั้งค่าระบบ' },
  { value: 'Promotion', label: 'โปรโมชั่น' },
  { value: 'DepositWithdraw', label: 'ฝาก-ถอน' },
  { value: 'BankAccount', label: 'บัญชีธนาคาร' },
  { value: 'Notification', label: 'การแจ้งเตือน' },
  { value: 'System', label: 'ระบบ' },
]

// ============================================
// Helper Functions
// ============================================

const parseTimestamp = (timestamp: any): Date | null => {
  if (!timestamp) return null
  if (timestamp?.iso) return new Date(timestamp.iso)
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp)
    if (!isNaN(date.getTime())) return date
  }
  if (timestamp instanceof Date) return timestamp
  return null
}

const formatDate = (timestamp: any) => {
  const date = parseTimestamp(timestamp)
  if (!date) return '-'
  return date.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const formatTime = (timestamp: any) => {
  const date = parseTimestamp(timestamp)
  if (!date) return '-'
  return date.toLocaleTimeString('th-TH', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDateTime = (timestamp: any) => {
  const date = parseTimestamp(timestamp)
  if (!date) return '-'
  return date.toLocaleString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatChangeValue = (value: any): string => {
  if (value === null || value === undefined) return '-'
  if (value === '') return '(ว่าง)'
  if (typeof value === 'boolean') return value ? 'ใช่' : 'ไม่ใช่'
  if (typeof value === 'object') {
    try { return JSON.stringify(value, null, 2) } catch { return String(value) }
  }
  return String(value)
}

const getDefaultDateRange = () => {
  // ✅ ใช้ local date เพื่อหลีกเลี่ยงปัญหา timezone
  const today = new Date()
  const sevenDaysAgo = new Date()
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
  
  // ✅ Format เป็น YYYY-MM-DD โดยใช้ local time
  const formatDateLocal = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  
  return {
    dateFrom: formatDateLocal(sevenDaysAgo),
    dateTo: formatDateLocal(today), // ✅ วันปัจจุบัน (local time)
  }
}

const getActionBadgeVariant = (action: AuditAction) => {
  const variants: Record<string, string> = {
    login: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    logout: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
    create: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    update: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    delete: 'bg-red-500/10 text-red-600 dark:text-red-400',
    update_setting: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    approve: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    reject: 'bg-red-500/10 text-red-600 dark:text-red-400',
    deposit: 'bg-green-500/10 text-green-600 dark:text-green-400',
    withdraw: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  }
  return variants[action] || 'bg-slate-500/10 text-slate-600'
}

const getActionIcon = (action: AuditAction) => {
  const icons: Record<string, React.ReactNode> = {
    login: <LogIn className="w-3.5 h-3.5" />,
    logout: <LogOut className="w-3.5 h-3.5" />,
    create: <Plus className="w-3.5 h-3.5" />,
    update: <Edit className="w-3.5 h-3.5" />,
    delete: <Trash2 className="w-3.5 h-3.5" />,
    update_setting: <Settings className="w-3.5 h-3.5" />,
    approve: <CheckCircle className="w-3.5 h-3.5" />,
    reject: <XCircle className="w-3.5 h-3.5" />,
    deposit: <Wallet className="w-3.5 h-3.5" />,
    withdraw: <Wallet className="w-3.5 h-3.5" />,
  }
  return icons[action] || <FileText className="w-3.5 h-3.5" />
}

// ✅ ฟังก์ชันสำหรับคัดลอก Fingerprint ID
const copyFingerprintId = async (log: AuditLog) => {
  if (!log.fingerprintId) {
    toast.error('ไม่มี Fingerprint ID')
    return
  }

  try {
    await navigator.clipboard.writeText(log.fingerprintId)
    toast.success(`คัดลอก Fingerprint ID: ${log.fingerprintId.substring(0, 8)}...`)
  } catch (error) {
    toast.error('ไม่สามารถคัดลอกได้')
  }
}

// ============================================
// Main Component
// ============================================

export function AuditLogPage() {
  const { t: _t } = useTranslation()
  void _t // Suppress unused warning - kept for future i18n
  const { sessionToken } = useAuth()
  
  // State
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [stats, setStats] = useState<AuditLogStatistics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  
  // Filters - ✅ ใช้ useMemo เพื่อให้อัปเดตทุกครั้งที่ component render
  const defaultDates = useMemo(() => getDefaultDateRange(), [])
  const [filters, setFilters] = useState<AuditLogFilters>({
    dateFrom: defaultDates.dateFrom,
    dateTo: defaultDates.dateTo,
  })
  
  // ✅ อัปเดต dateTo เป็นวันปัจจุบันทุกครั้งที่ component mount
  useEffect(() => {
    const formatDateLocal = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    
    const today = formatDateLocal(new Date())
    if (filters.dateTo !== today) {
      console.log(`🔄 [AuditLog] Updating dateTo from ${filters.dateTo} to ${today}`)
      setFilters(prev => ({ ...prev, dateTo: today }))
    }
  }, []) // ✅ รันครั้งเดียวตอน mount
  const [searchText, setSearchText] = useState('')
  const [_showFilters, _setShowFilters] = useState(false)
  void _showFilters; void _setShowFilters // Suppress unused warning - kept for future use
  
  // Detail Dialog
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)
  
  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 300)
    return () => clearTimeout(timer)
  }, [searchText])
  
  // ============================================
  // Data Fetching
  // ============================================
  
  const loadData = useCallback(async () => {
    if (!sessionToken) return
    
    setIsLoading(true)
    try {
      const service = getAuditLogService()
      
      // ✅ ถ้า search text เป็น fingerprint ID (ยาวกว่า 20 ตัวอักษร) ให้ใช้ fingerprintId filter แทน
      const searchTextToUse = debouncedSearch || undefined
      const fingerprintIdFromSearch = searchTextToUse && searchTextToUse.length > 20 
        ? searchTextToUse.trim() 
        : undefined
      
      const finalFilters = fingerprintIdFromSearch
        ? { ...filters, fingerprintId: fingerprintIdFromSearch, searchText: undefined }
        : { ...filters, searchText: searchTextToUse }
      
      const [logsResult, statsResult] = await Promise.all([
        service.findAuditLogs(
          sessionToken,
          { page, limit },
          finalFilters
        ),
        service.getStatistics(sessionToken, filters),
      ])
      
      setLogs(logsResult.results)
      setTotalCount(logsResult.count)
      setStats(statsResult)
      
    } catch (error) {
      console.error('Failed to load audit logs:', error)
    } finally {
      setIsLoading(false)
    }
  }, [sessionToken, page, limit, filters, debouncedSearch])
  
  useEffect(() => {
    loadData()
  }, [loadData])
  
  // ============================================
  // Handlers
  // ============================================
  
  const handleFilterChange = (key: keyof AuditLogFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value || undefined }))
    setPage(1)
  }
  
  const clearFilters = () => {
    const freshDates = getDefaultDateRange() // ✅ เรียกใหม่ทุกครั้งเพื่อให้ได้วันปัจจุบัน
    setFilters({ dateFrom: freshDates.dateFrom, dateTo: freshDates.dateTo })
    setSearchText('')
    setPage(1)
  }
  
  const activeFiltersCount = Object.entries(filters).filter(([k, v]) => {
    if (k === 'dateFrom' && v === defaultDates.dateFrom) return false
    if (k === 'dateTo' && v === defaultDates.dateTo) return false
    return v
  }).length + (searchText ? 1 : 0)
  
  const totalPages = Math.ceil(totalCount / limit)
  
  // Export to CSV
  const exportToCSV = () => {
    if (logs.length === 0) return
    const headers = ['เวลา', 'ผู้ดำเนินการ', 'Username', 'การกระทำ', 'ประเภท', 'ชื่อ', 'คำอธิบาย', 'สถานะ']
    const rows = logs.map(log => [
      formatDateTime(log.timestamp),
      log.actorName,
      log.actorUsername,
      AuditActionLabels[log.action] || log.action,
      EntityTypeLabels[log.entityType] || log.entityType,
      log.entityName,
      log.description,
      log.status === 'success' ? 'สำเร็จ' : 'ล้มเหลว',
    ])
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }
  
  // ============================================
  // Render
  // ============================================
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">ประวัติการใช้งาน</h1>
          <p className="text-muted-foreground text-sm">ติดตามและตรวจสอบการเปลี่ยนแปลงในระบบ</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="gap-1.5">
            <Activity className="h-3.5 w-3.5" />
            {totalCount} รายการ
          </Badge>
          <Button variant="outline" size="sm" onClick={loadData} disabled={isLoading}>
            <RefreshCw className={cn("h-4 w-4 mr-1.5", isLoading && "animate-spin")} />
            รีเฟรช
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-blue-500/10">
                  <History className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">ทั้งหมด</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-emerald-500/10">
                  <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{stats.byStatus?.success || 0}</p>
                  <p className="text-xs text-muted-foreground">สำเร็จ</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-red-500/10">
                  <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.byStatus?.failed || 0}</p>
                  <p className="text-xs text-muted-foreground">ล้มเหลว</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-amber-500/10">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats.bySeverity?.warning || 0}</p>
                  <p className="text-xs text-muted-foreground">คำเตือน</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filters & Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchText}
                onChange={(e) => { setSearchText(e.target.value); setPage(1) }}
                placeholder="ค้นหาจากคำอธิบาย, ผู้ดำเนินการ, Fingerprint ID..."
                className="pl-9"
              />
            </div>
            
            {/* Fingerprint ID Filter */}
            <div className="relative">
              <Input
                value={filters.fingerprintId || ''}
                onChange={(e) => handleFilterChange('fingerprintId', e.target.value)}
                placeholder="Fingerprint ID"
                className="w-[180px] font-mono text-xs"
              />
            </div>
            
            {/* Quick Filters */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Date Range */}
              <div className="flex items-center gap-2">
                <div className="w-[180px]">
                  <ThaiDatePicker
                    selected={filters.dateFrom ? new Date(filters.dateFrom) : null}
                    onChange={(date) => handleFilterChange('dateFrom', date ? toDateInputValue(date) : '')}
                    placeholder="วันที่เริ่มต้น"
                  />
                </div>
                <span className="text-muted-foreground">-</span>
                <div className="w-[180px]">
                  <ThaiDatePicker
                    selected={filters.dateTo ? new Date(filters.dateTo) : null}
                    onChange={(date) => handleFilterChange('dateTo', date ? toDateInputValue(date) : '')}
                    placeholder="วันที่สิ้นสุด"
                  />
                </div>
              </div>
              
              {/* Action Filter */}
              <Select
                value={filters.action || '__all__'}
                onValueChange={(v) => handleFilterChange('action', v === '__all__' ? '' : v)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="การกระทำ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">ทุกการกระทำ</SelectItem>
                  {ACTIONS.map(action => (
                    <SelectItem key={action.value} value={action.value}>{action.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Entity Filter */}
              <Select
                value={filters.entityType || '__all__'}
                onValueChange={(v) => handleFilterChange('entityType', v === '__all__' ? '' : v)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="ประเภท" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">ทุกประเภท</SelectItem>
                  {ENTITY_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Status Filter */}
              <Select
                value={filters.status || '__all__'}
                onValueChange={(v) => handleFilterChange('status', v === '__all__' ? '' : v)}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="สถานะ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">ทุกสถานะ</SelectItem>
                  <SelectItem value="success">สำเร็จ</SelectItem>
                  <SelectItem value="failed">ล้มเหลว</SelectItem>
                </SelectContent>
              </Select>
              
              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  ล้าง
                </Button>
              )}
              
              <Button variant="outline" size="sm" onClick={exportToCSV} disabled={logs.length === 0}>
                <Download className="h-4 w-4 mr-1.5" />
                ส่งออก
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <History className="h-12 w-12 text-muted-foreground/50 mb-3" />
              <p className="font-medium">ไม่พบข้อมูล</p>
              <p className="text-sm text-muted-foreground">ลองเปลี่ยนเงื่อนไขการค้นหา</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 font-medium text-muted-foreground text-sm">เวลา</th>
                      <th className="text-left p-4 font-medium text-muted-foreground text-sm">ผู้ดำเนินการ</th>
                      <th className="text-left p-4 font-medium text-muted-foreground text-sm">การกระทำ</th>
                      <th className="text-left p-4 font-medium text-muted-foreground text-sm">ประเภท</th>
                      <th className="text-left p-4 font-medium text-muted-foreground text-sm">คำอธิบาย</th>
                      <th className="text-center p-4 font-medium text-muted-foreground text-sm">สถานะ</th>
                      <th className="text-center p-4 font-medium text-muted-foreground text-sm w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-muted/50 transition-colors">
                        <td className="p-4">
                          <div className="text-sm font-medium">{formatDate(log.timestamp)}</div>
                          <div className="text-xs text-muted-foreground">{formatTime(log.timestamp)}</div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-medium">
                              {log.actorName?.charAt(0) || '?'}
                            </div>
                            <div>
                              <div className="text-sm font-medium">{log.actorName}</div>
                              <div className="text-xs text-muted-foreground">@{log.actorUsername}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant="secondary" className={cn("gap-1", getActionBadgeVariant(log.action))}>
                            {getActionIcon(log.action)}
                            {AuditActionLabels[log.action] || log.action}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="text-sm">{EntityTypeLabels[log.entityType] || log.entityType}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[150px]">{log.entityName}</div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm truncate max-w-[250px]">{log.description}</div>
                          {log.changes && log.changes.length > 0 && (
                            <div className="text-xs text-muted-foreground">{log.changes.length} การเปลี่ยนแปลง</div>
                          )}
                          {log.fingerprintId ? (
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className="text-xs text-muted-foreground font-mono">
                                FP: {log.fingerprintId.length > 8 ? `${log.fingerprintId.substring(0, 8)}...` : log.fingerprintId}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  copyFingerprintId(log)
                                }}
                                className="p-0.5 rounded hover:bg-muted/80 text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                                title="คัดลอก Fingerprint ID"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            // ✅ แสดงเพื่อ debug (ถ้าไม่มี fingerprintId)
                            <div className="text-xs text-muted-foreground/50 font-mono mt-1 italic">
                              FP: ไม่มีข้อมูล
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {log.status === 'success' ? (
                            <CheckCircle className="h-5 w-5 text-emerald-500 mx-auto" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <Button variant="ghost" size="icon" onClick={() => setSelectedLog(log)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {logs.map((log) => (
                  <div key={log.id} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-medium">
                          {log.actorName?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{log.actorName}</div>
                          <div className="text-xs text-muted-foreground">{formatDateTime(log.timestamp)}</div>
                        </div>
                      </div>
                      {log.status === 'success' ? (
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <Badge variant="secondary" className={cn("gap-1 mb-2", getActionBadgeVariant(log.action))}>
                      {getActionIcon(log.action)}
                      {AuditActionLabels[log.action] || log.action}
                    </Badge>
                    <p className="text-sm line-clamp-2 mb-2">{log.description}</p>
                    {log.fingerprintId && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="text-xs text-muted-foreground font-mono">
                          FP: {log.fingerprintId.length > 8 ? `${log.fingerprintId.substring(0, 8)}...` : log.fingerprintId}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            copyFingerprintId(log)
                          }}
                          className="p-0.5 rounded hover:bg-muted/80 text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                          title="คัดลอก Fingerprint ID"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{EntityTypeLabels[log.entityType]}</span>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedLog(log)}>
                        <Eye className="h-4 w-4 mr-1" /> ดู
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    แสดง {((page - 1) * limit) + 1} - {Math.min(page * limit, totalCount)} จาก {totalCount} รายการ
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">แสดง</span>
                    <Select
                      value={String(limit)}
                      onValueChange={(v) => { setLimit(Number(v)); setPage(1) }}
                    >
                      <SelectTrigger className="w-[70px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">รายการ</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                  >
                    หน้าแรก
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm px-2 min-w-[100px] text-center">
                    หน้า {page} / {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(totalPages)}
                    disabled={page >= totalPages}
                  >
                    หน้าสุดท้าย
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Detail Dialog */}
      <AuditLogDetailDialog
        log={selectedLog}
        open={!!selectedLog}
        onOpenChange={(open) => !open && setSelectedLog(null)}
      />
    </div>
  )
}

// ============================================
// Detail Dialog Component
// ============================================

interface AuditLogDetailDialogProps {
  log: AuditLog | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function AuditLogDetailDialog({ log, open, onOpenChange }: AuditLogDetailDialogProps) {
  if (!log) return null
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
              {log.actorName?.charAt(0) || '?'}
            </div>
            <div>
              <div className="font-semibold">{log.actorName}</div>
              <div className="text-sm font-normal text-muted-foreground">
                {formatDateTime(log.timestamp)}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Action & Status Badges */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className={cn("gap-1", getActionBadgeVariant(log.action))}>
              {getActionIcon(log.action)}
              {AuditActionLabels[log.action] || log.action}
            </Badge>
            <Badge variant={log.status === 'success' ? 'default' : 'destructive'}>
              {log.status === 'success' ? 'สำเร็จ' : 'ล้มเหลว'}
            </Badge>
            <Badge variant="outline">{SeverityLabels[log.severity]}</Badge>
          </div>
          
          {/* Description */}
          <div className="p-4 rounded-lg bg-muted/50">
            <p className="text-sm font-medium text-muted-foreground mb-1">คำอธิบาย</p>
            <p>{log.description}</p>
          </div>
          
          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground mb-0.5">ประเภทข้อมูล</p>
              <p className="font-medium">{EntityTypeLabels[log.entityType] || log.entityType}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground mb-0.5">ชื่อข้อมูล</p>
              <p className="font-medium">{log.entityName || '-'}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground mb-0.5">Username</p>
              <p className="font-medium">@{log.actorUsername}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground mb-0.5">บทบาท</p>
              <p className="font-medium">{log.actorRole === 'super_admin' ? 'Super Admin' : log.actorRole}</p>
            </div>
          </div>
          
          {/* Changes */}
          {log.changes && log.changes.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2 flex items-center gap-2">
                <Edit className="h-4 w-4 text-amber-500" />
                การเปลี่ยนแปลง ({log.changes.length} รายการ)
              </p>
              <div className="space-y-2">
                {log.changes.map((change, index) => (
                  <div key={index} className="p-3 rounded-lg border bg-card">
                    <p className="text-sm font-medium mb-2">{change.fieldLabel || change.field}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="p-2 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <span className="text-xs text-red-600 dark:text-red-400">ก่อน</span>
                        <p className="text-red-700 dark:text-red-300 line-through">{formatChangeValue(change.oldValue)}</p>
                      </div>
                      <div className="p-2 rounded bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                        <span className="text-xs text-emerald-600 dark:text-emerald-400">หลัง</span>
                        <p className="text-emerald-700 dark:text-emerald-300 font-medium">{formatChangeValue(change.newValue)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Technical Info */}
          <div className="p-3 rounded-lg bg-muted/30">
            <p className="text-xs text-muted-foreground mb-1">ข้อมูลทางเทคนิค</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">IP: </span>
                <span className="font-mono">{log.ipAddress || '-'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">ID: </span>
                <span className="font-mono text-xs">{log.id}</span>
              </div>
              {log.fingerprintId && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Fingerprint ID: </span>
                  <span className="font-mono text-xs">{log.fingerprintId}</span>
                </div>
              )}
              {log.deviceId && (
                <div>
                  <span className="text-muted-foreground">Device ID: </span>
                  <span className="font-mono text-xs">{log.deviceId}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Error Message */}
          {log.errorMessage && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">ข้อผิดพลาด</p>
              <p className="text-red-700 dark:text-red-300">{log.errorMessage}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
